import { useCallback, useEffect, useState } from "react";
import { apagar, del, get, patch, post } from "../lib/api";
import { useAuth } from "../lib/auth";
import { moeda } from "../lib/formato";
import { Confirmacao, Modal } from "../components/Modal";
import { periodoDaLinha, ROTULO_PERIODO_VALOR } from "../lib/periodoValor";
import type { Curso, PeriodoValor, TabelaValor, TipoTabelaValor } from "../lib/tipos";

/** Uma aba por tipo — substitui os três modais separados do 4.x. */
const ABAS: Array<{
  tipo: TipoTabelaValor;
  titulo: string;
  dica: React.ReactNode;
  exemploDescricao: string;
  exemploValor: string;
}> = [
  {
    tipo: "MENSALIDADE",
    titulo: "Mensalidade",
    dica: <>Aparecem como opções nos campos <strong>"Valor Normal — Mensalidade"</strong> (parcial e integral) do formulário de visita.</>,
    exemploDescricao: "Ex.: Mensalidade Bilíngue",
    exemploValor: "Ex.: 1250.00",
  },
  {
    tipo: "MATERIAL",
    titulo: "Material",
    dica: <>Aparecem como opções no campo <strong>"Material COC"</strong> do formulário de visita.</>,
    exemploDescricao: "Ex.: Material Fundamental I",
    exemploValor: "Ex.: 850.00",
  },
  {
    tipo: "BILINGUE",
    titulo: "Material Bilíngue",
    dica: <>Aparecem como opções no campo <strong>"Material Bilíngue"</strong> do formulário de visita.</>,
    exemploDescricao: "Ex.: Bilíngue Inglês",
    exemploValor: "Ex.: 350.00",
  },
];
// Os pisos comerciais não entram aqui: eles pertencem a uma linha de
// mensalidade, não a um curso, e têm tela própria (`ValoresMinimos`).

interface Rascunho {
  id?: string;
  descricao: string;
  valor: string;
  cursoId: string;
  /** "" = deixar o sistema deduzir pela descrição. */
  periodoValor: "" | PeriodoValor;
}

export default function Valores({
  tipo: tipoFixo,
}: {
  /** Trava a tela numa aba só — é assim que as rotas do menu entram. */
  tipo?: TipoTabelaValor;
} = {}) {
  const { podeAoMenos } = useAuth();
  const podeEditar = podeAoMenos("GESTOR");
  // desativar qualquer gestor faz; apagar de vez é irreversível
  const podeApagar = podeAoMenos("ADMIN");

  const [aba, setAba] = useState<TipoTabelaValor>(tipoFixo ?? "MENSALIDADE");
  const [itens, setItens] = useState<TabelaValor[]>([]);
  const [cursos, setCursos] = useState<Curso[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [editando, setEditando] = useState<Rascunho | null>(null);
  const [erroForm, setErroForm] = useState("");
  const [salvando, setSalvando] = useState(false);
  const [removendo, setRemovendo] = useState<TabelaValor | null>(null);
  const [apagando, setApagando] = useState<TabelaValor | null>(null);

  const carregar = useCallback(() => {
    setCarregando(true);
    return Promise.all([
      get<TabelaValor[]>(`/api/tabelas/valores?tipo=${aba}&incluirInativos=${mostrarInativos}`),
      get<Curso[]>("/api/tabelas/cursos"),
    ])
      .then(([v, c]) => { setItens(v); setCursos(c); setErro(""); })
      .catch((e) => setErro(e.message))
      .finally(() => setCarregando(false));
  }, [aba, mostrarInativos]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => { if (tipoFixo) setAba(tipoFixo); }, [tipoFixo]);

  const abaAtual = ABAS.find((a) => a.tipo === aba)!;

  async function salvar() {
    if (!editando) return;
    setErroForm("");
    const valor = Number(editando.valor.replace(",", "."));
    if (editando.valor.trim() === "") return setErroForm("Informe o valor (R$).");
    if (!Number.isFinite(valor) || valor < 0) return setErroForm("Valor inválido.");
    if (!editando.descricao.trim()) return setErroForm("Informe uma descrição.");

    setSalvando(true);
    try {
      const corpo = {
        tipo: aba,
        descricao: editando.descricao.trim(),
        valor,
        cursoId: editando.cursoId || null,
        // só mensalidade tem turno; nos outros tipos o campo não significa nada
        periodoValor: aba === "MENSALIDADE" ? editando.periodoValor || null : null,
      };
      if (editando.id) await patch(`/api/tabelas/valores/${editando.id}`, corpo);
      else await post("/api/tabelas/valores", corpo);
      setEditando(null);
      await carregar();
    } catch (e) {
      setErroForm(e instanceof Error ? e.message : "Falha ao salvar");
    } finally {
      setSalvando(false);
    }
  }

  async function remover() {
    if (!removendo) return;
    try {
      await del(`/api/tabelas/valores/${removendo.id}`);
      setRemovendo(null);
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Falha ao desativar");
      setRemovendo(null);
    }
  }

  /** Some do banco: para linha cadastrada errado ou duplicada. */
  async function apagarDeVez() {
    if (!apagando) return;
    try {
      await apagar(`/api/tabelas/valores/${apagando.id}`);
      setApagando(null);
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Falha ao apagar");
      setApagando(null);
    }
  }

  async function reativar(v: TabelaValor) {
    try {
      await patch(`/api/tabelas/valores/${v.id}`, { ativo: true });
      await carregar();
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Falha ao reativar");
    }
  }

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <h2 style={{ fontSize: 18, margin: 0 }}>
          {tipoFixo ? `Valores — ${abaAtual.titulo}` : "Tabelas de valores"}
        </h2>
        {podeEditar && (
          <button className="botao" onClick={() => { setErroForm(""); setEditando({ descricao: "", valor: "", cursoId: "", periodoValor: "" }); }}>
            Novo valor
          </button>
        )}
      </div>
      <p style={{ fontSize: 13, color: "var(--texto-suave)", margin: "0 0 16px" }}>{abaAtual.dica}</p>

      <div style={{ display: "flex", gap: 4, borderBottom: "1px solid #eaecf0", marginBottom: 16 }}>
        {(tipoFixo ? ABAS.filter((a) => a.tipo === tipoFixo) : ABAS).map((a) => (
          <button
            key={a.tipo}
            onClick={() => setAba(a.tipo)}
            style={{
              padding: "8px 16px", border: "none", background: "none", cursor: "pointer", fontSize: 14,
              fontWeight: aba === a.tipo ? 600 : 400,
              color: aba === a.tipo ? "var(--verde)" : "var(--texto-suave)",
              borderBottom: `2px solid ${aba === a.tipo ? "var(--verde)" : "transparent"}`,
              marginBottom: -1,
            }}
          >
            {a.titulo}
          </button>
        ))}
        <label style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--texto-suave)", paddingBottom: 8 }}>
          <input
            type="checkbox"
            checked={mostrarInativos}
            onChange={(e) => setMostrarInativos(e.target.checked)}
            style={{ accentColor: "var(--verde)" }}
          />
          Mostrar desativados
        </label>
      </div>

      {erro && <div className="erro" style={{ marginBottom: 12 }}>{erro}</div>}

      <div className="cartao" style={{ padding: 0, overflowX: "auto" }}>
        <table>
          <thead>
            <tr>
              <th>Descrição</th>
              <th style={{ width: 160 }}>Valor</th>
              <th style={{ width: 220 }}>Curso</th>
              <th style={{ width: 170 }}></th>
            </tr>
          </thead>
          <tbody>
            {itens.map((v) => (
              <tr key={v.id} style={v.ativo === false ? { opacity: 0.55 } : undefined}>
                <td>
                  {v.descricao}
                  {aba === "MENSALIDADE" && (() => {
                    const p = periodoDaLinha(v);
                    return (
                      <span
                        title={
                          v.periodoValor
                            ? "Período definido no cadastro"
                            : p
                              ? "Período deduzido da descrição — edite a linha para fixar"
                              : "Sem período: este valor aparece nos dois blocos, 4A e 4B"
                        }
                        style={{
                          fontSize: 9, fontWeight: 700, borderRadius: 4, padding: "1px 5px",
                          marginLeft: 6, verticalAlign: 1,
                          background: p ? (v.periodoValor ? "#ecfdf3" : "#f9fafb") : "#fffaeb",
                          color: p ? (v.periodoValor ? "#0e8c3a" : "#667085") : "#b54708",
                          border: `1px solid ${p ? (v.periodoValor ? "#c6e9d4" : "#eaecf0") : "#fedf89"}`,
                        }}
                      >
                        {p ? ROTULO_PERIODO_VALOR[p].toUpperCase() : "SEM PERÍODO"}
                        {p && !v.periodoValor ? " ?" : ""}
                      </span>
                    );
                  })()}
                  {v.ativo === false && (
                    <span style={{ fontSize: 11, color: "#b42318", marginLeft: 6 }}>desativado</span>
                  )}
                </td>
                <td><strong>{moeda(v.valor)}</strong></td>
                <td style={{ color: "var(--texto-suave)" }}>{v.curso?.nome ?? "Todos os cursos"}</td>
                <td style={{ whiteSpace: "nowrap" }}>
                  {podeEditar && (
                    <>
                      <button
                        className="botao secundario"
                        style={{ padding: "5px 10px", fontSize: 12 }}
                        onClick={() => {
                          setErroForm("");
                          setEditando({
                            id: v.id, descricao: v.descricao, valor: String(v.valor),
                            cursoId: v.curso?.id ?? "", periodoValor: v.periodoValor ?? "",
                          });
                        }}
                      >
                        Editar
                      </button>
                      {v.ativo === false ? (
                        <button
                          className="botao secundario"
                          style={{ padding: "5px 10px", fontSize: 12, marginLeft: 6, color: "var(--verde)" }}
                          onClick={() => reativar(v)}
                        >
                          Reativar
                        </button>
                      ) : (
                        <button
                          className="botao secundario"
                          style={{ padding: "5px 10px", fontSize: 12, marginLeft: 6, color: "#b42318" }}
                          onClick={() => setRemovendo(v)}
                        >
                          Desativar
                        </button>
                      )}
                      {podeApagar && (
                        <button
                          onClick={() => setApagando(v)}
                          title="Apagar de vez — para linha cadastrada errado ou duplicada"
                          style={{ background: "none", border: "none", cursor: "pointer", fontSize: 15, padding: 4, marginLeft: 4, opacity: .55 }}
                          onMouseEnter={(ev) => { ev.currentTarget.style.opacity = "1"; }}
                          onMouseLeave={(ev) => { ev.currentTarget.style.opacity = ".55"; }}
                        >
                          🗑️
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
            {!carregando && itens.length === 0 && (
              <tr><td colSpan={4} style={{ textAlign: "center", color: "var(--texto-suave)", padding: 32 }}>
                Nenhum valor cadastrado em {abaAtual.titulo}.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editando && (
        <Modal
          titulo={editando.id ? `Editar valor — ${abaAtual.titulo}` : `Novo valor — ${abaAtual.titulo}`}
          largura={520}
          onFechar={() => setEditando(null)}
          rodape={
            <>
              <button className="botao secundario" onClick={() => setEditando(null)} disabled={salvando}>Cancelar</button>
              <button className="botao" onClick={salvar} disabled={salvando}>{salvando ? "Salvando..." : "Salvar"}</button>
            </>
          }
        >
          {erroForm && <div className="erro" style={{ marginBottom: 12 }}>{erroForm}</div>}

          <label className="rotulo">Descrição</label>
          <input
            className="campo"
            placeholder={abaAtual.exemploDescricao}
            value={editando.descricao}
            onChange={(e) => setEditando({ ...editando, descricao: e.target.value })}
          />

          <label className="rotulo" style={{ marginTop: 12 }}>Valor (R$)</label>
          <input
            className="campo"
            type="number"
            step="0.01"
            min="0"
            placeholder={abaAtual.exemploValor}
            value={editando.valor}
            onChange={(e) => setEditando({ ...editando, valor: e.target.value })}
          />

          {aba === "MENSALIDADE" && (
            <>
              <label className="rotulo" style={{ marginTop: 12 }}>Período</label>
              <select
                className="campo"
                value={editando.periodoValor}
                onChange={(e) => setEditando({ ...editando, periodoValor: e.target.value as "" | PeriodoValor })}
              >
                <option value="">Deduzir pela descrição</option>
                <option value="PARCIAL">Parcial — aparece só no bloco 4A</option>
                <option value="INTEGRAL">Integral — aparece só no bloco 4B</option>
              </select>
              <div style={{ fontSize: 11, color: "var(--texto-suave)", marginTop: 4, lineHeight: 1.6 }}>
                {editando.periodoValor
                  ? "O formulário oferece este valor só no bloco correspondente."
                  : `Sem escolher, o sistema lê a descrição: hoje ela daria ${
                      periodoDaLinha({ descricao: editando.descricao, periodoValor: null })
                        ? ROTULO_PERIODO_VALOR[periodoDaLinha({ descricao: editando.descricao, periodoValor: null })!]
                        : "indefinido — e aí o valor aparece nos dois blocos"
                    }.`}
              </div>
            </>
          )}

          <label className="rotulo" style={{ marginTop: 12 }}>Curso</label>
          <select
            className="campo"
            value={editando.cursoId}
            onChange={(e) => setEditando({ ...editando, cursoId: e.target.value })}
          >
            <option value="">Todos os cursos</option>
            {cursos.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
          </select>
          <div style={{ fontSize: 11, color: "var(--texto-suave)", marginTop: 4 }}>
            Se vincular a um curso, o valor só aparece no formulário quando esse curso estiver selecionado.
          </div>
        </Modal>
      )}

      {removendo && (
        <Confirmacao
          titulo="Desativar valor"
          perigo
          rotuloConfirmar="Desativar"
          mensagem={
            <>
              Desativar <strong>{removendo.descricao}</strong> ({moeda(removendo.valor)})?
              <div style={{ marginTop: 8, color: "var(--texto-suave)", fontSize: 13 }}>
                Ele deixa de aparecer no formulário, mas as visitas que já usam esse valor continuam intactas.
              </div>
            </>
          }
          onConfirmar={remover}
          onCancelar={() => setRemovendo(null)}
        />
      )}

      {apagando && (
        <Confirmacao
          titulo="Apagar de vez?"
          perigo
          rotuloConfirmar="Apagar"
          mensagem={
            <>
              <strong>{apagando.descricao}</strong> ({moeda(apagando.valor)})
              <div style={{ marginTop: 10, color: "var(--texto-suave)", fontSize: 13, lineHeight: 1.6 }}>
                Some do banco, nem aparece em "Mostrar desativados". Leva junto o valor mínimo
                vinculado a esta linha, se houver.
                <br />
                <strong>As visitas e propostas já feitas não mudam</strong> — elas guardam uma cópia
                do valor, não um vínculo com esta tabela.
              </div>
              <div style={{ marginTop: 10, fontSize: 13, color: "#b42318" }}>
                Não há como desfazer. Para apenas tirar do formulário, use <strong>Desativar</strong>.
              </div>
            </>
          }
          onConfirmar={apagarDeVez}
          onCancelar={() => setApagando(null)}
        />
      )}
    </>
  );
}
